// Admin tab: LCL Tariff Tiers Import.
// Upload a FAM LCL tariff Excel (+ the CargoFive charges JSON), convert it into
// Monada rates with the full BAS CBM-tier ladder restored (CargoFive only keeps
// the first band), preview the result, then add the rates to a chosen workspace.
//
// Pipeline: Excel -> tierMap (parsed in-browser) + charges JSON -> server
// /api/admin/lcl-tariff/convert (runs the validated CargoFive->rates converter
// and enriches BAS tiers) -> preview -> /api/rates/bulk into the chosen org.
import { useState, useCallback, useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';

import axios from 'axios';
import dayjs from 'dayjs';
import { useDropzone } from 'react-dropzone';

import {
    Box, Typography, Button, CircularProgress, Alert, Card, CardContent,
    Dialog, DialogTitle, DialogContent, DialogActions, Chip, TextField,
    Table, TableBody, TableRow, TableCell, TableHead, TableContainer, Paper,
    FormControl, InputLabel, Select, MenuItem, Checkbox, FormControlLabel,
} from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCloudArrowUp } from '@fortawesome/pro-light-svg-icons';

import { parseTariffWorkbook } from './lclTariffExcel.js';
import { getRatesFromServer } from '../dashboard/utils/rates.js';

const PREVIEW_LIMIT = 150;
const CHUNK = 25;

// Parse a CargoFive charges file: a flat JSON array, a single {data:[...]} page,
// or several paginated {data:[...]} objects concatenated (raw API dump).
function parseCharges(text) {
    const trimmed = text.trim();
    try {
        const v = JSON.parse(trimmed);
        if (Array.isArray(v)) return v;
        if (v && Array.isArray(v.data)) return v.data;
    } catch (e) { /* fall through to multi-object parse */ }
    const charges = [];
    const re = /\}\s*\{/g;
    // split concatenated top-level objects
    let depth = 0, start = 0, inStr = false, esc = false;
    for (let i = 0; i < trimmed.length; i++) {
        const ch = trimmed[i];
        if (inStr) { if (esc) esc = false; else if (ch === '\\') esc = true; else if (ch === '"') inStr = false; continue; }
        if (ch === '"') inStr = true;
        else if (ch === '{') { if (depth === 0) start = i; depth++; }
        else if (ch === '}') { depth--; if (depth === 0) { const obj = JSON.parse(trimmed.slice(start, i + 1)); if (Array.isArray(obj.data)) charges.push(...obj.data); else if (obj.routes) charges.push(obj); } }
    }
    void re;
    return charges;
}

function fmtDate(v) {
    if (v == null || v === '') return '';
    const d = typeof v === 'number' || /^\d{10,13}$/.test(String(v)) ? dayjs(Number(v) < 1e12 ? Number(v) * 1000 : Number(v)) : dayjs(v);
    return d.isValid() ? d.format('YYYY-MM-DD') : String(v);
}

// title -> display string ("=formula" or "value|CUR") for every charge field on a rate.
function rateFieldMap(rate) {
    const m = {};
    for (const s of (rate.offer?.sections || [])) {
        for (const f of (s.offers?.[0]?.fields || [])) {
            const cell = f.values?.[Object.keys(f.values || {})[0]];
            if (!cell) { m[f.title] = ''; continue; }
            m[f.title] = cell.formula ? cell.formula : (cell.value != null ? `${cell.value}${cell.currency ? '|' + cell.currency : ''}` : '');
        }
    }
    return m;
}

function LclTariffTiers() {
    const { accesstoken } = useOutletContext();
    const [excelFile, setExcelFile] = useState(null);
    const [jsonFile, setJsonFile] = useState(null);
    const [processing, setProcessing] = useState(null);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);          // { rates, stats }
    const [rates, setRates] = useState([]);              // editable copy used for preview + add
    const [previewOpen, setPreviewOpen] = useState(false);

    const [orgs, setOrgs] = useState(null);
    const [orgId, setOrgId] = useState('');
    const [adding, setAdding] = useState(null);
    const [addResult, setAddResult] = useState(null);
    const [validFrom, setValidFrom] = useState('');
    const [validUntil, setValidUntil] = useState('');
    const [overrideExisting, setOverrideExisting] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const { data } = await axios(`/api/admin/organizations?accesstoken=${accesstoken}`);
                setOrgs(data);
            } catch (e) { /* non-fatal; user can still convert/preview */ }
        })();
    }, [accesstoken]);

    const onDrop = useCallback((acceptedFiles) => {
        setError(null);
        for (const f of acceptedFiles) {
            const n = f.name.toLowerCase();
            if (n.endsWith('.xlsx') || n.endsWith('.xls')) setExcelFile(f);
            else if (n.endsWith('.json')) setJsonFile(f);
        }
    }, []);

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        accept: { 'application/json': ['.json'], 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'], 'application/vnd.ms-excel': ['.xls'] },
        disabled: !!processing,
    });

    function readFile(file, as) {
        return new Promise((resolve, reject) => {
            const r = new FileReader();
            r.onload = () => resolve(r.result);
            r.onerror = () => reject(new Error('Failed to read ' + file.name));
            if (as === 'arrayBuffer') r.readAsArrayBuffer(file); else r.readAsText(file);
        });
    }

    async function onConvert() {
        setError(null);
        setResult(null);
        setAddResult(null);
        if (!excelFile) { setError('Please add the tariff Excel (.xlsx).'); return; }
        try {
            setProcessing('Parsing Excel...');
            const buf = await readFile(excelFile, 'arrayBuffer');
            const { tierMap, sheets, routeCount, surchargesByOrigin, surchargeRouteCount, unlabeledBlocks } = parseTariffWorkbook(buf);
            if (!routeCount) { setError('No tariff routes found in the Excel (expected sheets with an ORIGEM header and CBM band columns).'); setProcessing(null); return; }

            let charges = null;
            if (jsonFile) {
                setProcessing('Parsing CargoFive JSON...');
                const text = await readFile(jsonFile, 'text');
                charges = parseCharges(text);
                if (!charges.length) { setError('No charges found in the JSON file.'); setProcessing(null); return; }
            }

            setProcessing(charges ? `Converting ${routeCount} routes / ${charges.length} charges...` : `Building ${routeCount} routes from the Excel (${surchargeRouteCount} with prose surcharges)...`);
            const body = charges ? { charges, tierMap } : { tierMap, surchargesByOrigin, validFrom, validUntil };
            const { data } = await axios.post(`/api/admin/lcl-tariff/convert?accesstoken=${accesstoken}`, body);
            data.stats.sheets = sheets;
            if (!charges) data.stats.unlabeledBlocks = unlabeledBlocks;
            setResult(data);
            setRates(data.rates || []);
            setPreviewOpen(true);
            setProcessing(null);
        } catch (e) {
            setError(e.response?.data?.detail || e.message || 'Conversion failed.');
            setProcessing(null);
        }
    }

    // Edit a charge field's formula/value in the preview. `raw` is "=formula" or
    // "value|CUR" or a plain number; we store it back the way Monada expects.
    function editCharge(rateIndex, title, raw) {
        setRates(prev => {
            const next = prev.slice();
            const rate = { ...next[rateIndex] };
            rate.offer = { ...rate.offer, sections: (rate.offer?.sections || []).map(s => ({
                ...s,
                offers: (s.offers || []).map((o, oi) => oi !== 0 ? o : ({
                    ...o,
                    fields: (o.fields || []).map(f => {
                        if (f.title !== title) return f;
                        const pid = Object.keys(f.values || {})[0];
                        if (!pid) return f;
                        const v = String(raw).trim();
                        const cur = f.values[pid].currency;
                        let cell;
                        if (v.startsWith('=') || /[a-z?]/i.test(v.replace(/e[+-]?\d/i, ''))) {
                            cell = { currency: cur, formula: v.replace(/^=/, '').split('|')[0] };
                        } else {
                            const [num, c] = v.split('|');
                            cell = { currency: c || cur, value: parseFloat(num) || 0 };
                        }
                        return { ...f, values: { ...f.values, [pid]: cell } };
                    }),
                })),
            })) };
            next[rateIndex] = rate;
            return next;
        });
    }

    // Current calculation method for a charge column: 'per-unit-type' (per W/M)
    // or 'flat' (per shipment), read from the first rate that has the charge.
    function columnMethod(title) {
        for (const r of rates) {
            for (const s of (r.offer?.sections || [])) {
                for (const f of (s.offers?.[0]?.fields || [])) {
                    if (f.title === title) return f.type === 'flat' ? 'flat' : 'per-unit-type';
                }
            }
        }
        return 'per-unit-type';
    }

    // Switch a charge column between per-W/M and per-shipment. Relocates the value
    // between values[productId] (per-unit-type) and values.flat (flat).
    function changeMethod(title, type) {
        setRates(prev => prev.map(rate => {
            let touched = false;
            const pid = rate.product?.id;
            const sections = (rate.offer?.sections || []).map(s => ({
                ...s,
                offers: (s.offers || []).map((o, oi) => oi !== 0 ? o : ({
                    ...o,
                    fields: (o.fields || []).map(f => {
                        if (f.title !== title) return f;
                        const firstKey = Object.keys(f.values || {})[0];
                        if (!firstKey) return f;
                        touched = true;
                        const cell = f.values[firstKey];
                        const values = type === 'flat' ? { flat: cell } : { [pid || firstKey]: cell };
                        return { ...f, type, values };
                    }),
                })),
            }));
            return touched ? { ...rate, offer: { ...rate.offer, sections } } : rate;
        }));
    }

    async function onAdd() {
        if (!orgId || !rates.length) return;
        setAddResult(null);
        let ok = 0, failed = 0, deleted = 0;
        try {
            // Override: delete existing rates that match a new one on supplier +
            // route + transshipment + overlapping validity, before adding.
            if (overrideExisting) {
                setAdding('Loading existing rates...');
                const existing = (await getRatesFromServer({ mode: 'rates', accesstoken, organizationId: orgId, since: 0 })).filter(r => !r.deleted);
                const ts = (v) => {
                    if (v == null || v === '') return null;
                    const n = typeof v === 'number' ? v : (/^\d{10,13}$/.test(String(v)) ? Number(v) : Date.parse(v));
                    return Number.isNaN(n) ? null : (n < 1e12 ? n * 1000 : n);
                };
                const nrm = (s) => String(s || '').normalize('NFKD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z0-9]/g, '');
                // lenient: equal, or one normalized string contains the other (e.g.
                // "FAM" vs "FAM CARGO USA", "via MIAMI" vs "Miami"); empty == empty.
                const loose = (a, b) => { a = nrm(a); b = nrm(b); if (!a && !b) return true; if (!a || !b) return false; return a === b || a.includes(b) || b.includes(a); };
                const overlaps = (aF, aU, bF, bU) => (aF == null || aU == null || bF == null || bU == null) ? true : (aF <= bU && aU >= bF);
                // one spec per new rate; route + dest exact, supplier + transshipment lenient, validity overlapping
                const specs = rates.map(r => ({ src: r.source?.id, dst: r.destination?.id, sup: r.supplier?.organization, trans: r.offer?.transshipment, from: ts(r.offer?.validFrom), until: ts(r.offer?.validUntil) }));
                const toDelete = existing.filter(e => specs.some(s =>
                    e.source?.id === s.src && e.destination?.id === s.dst &&
                    loose(e.supplier?.organization, s.sup) &&
                    loose(e.offer?.transshipment, s.trans) &&
                    overlaps(ts(e.offer?.validFrom), ts(e.offer?.validUntil), s.from, s.until)
                ));
                if (toDelete.length) {
                    setAdding(`Removing ${toDelete.length} existing rate(s)...`);
                    for (let i = 0; i < toDelete.length; i += CHUNK) {
                        await axios.post(`/api/rates/admin-delete?organizationId=${orgId}&accesstoken=${accesstoken}`, { rates: toDelete.slice(i, i + CHUNK) });
                    }
                    deleted = toDelete.length;
                }
            }
            setAdding(`Adding 0 / ${rates.length}...`);
            for (let i = 0; i < rates.length; i += CHUNK) {
                const chunk = rates.slice(i, i + CHUNK);
                const { data } = await axios.post(
                    `/api/rates/bulk?organizationId=${orgId}&accesstoken=${accesstoken}`,
                    { rates: chunk }
                );
                ok += (data.successful || []).length;
                failed += (data.failed || []).length;
                setAdding(`Adding ${Math.min(i + CHUNK, rates.length)} / ${rates.length}...`);
            }
            const org = (orgs || []).find(o => o.id === orgId);
            setAddResult({ ok, failed, deleted, org: org?.alias || orgId });
            setAdding(null);
        } catch (e) {
            setAddResult({ ok, failed, deleted, error: e.response?.data?.detail || e.message });
            setAdding(null);
        }
    }

    const stats = result?.stats;

    // union of all non-BAS charge titles across rates, first-seen order -> columns
    const surchargeTitles = useMemo(() => {
        const seen = new Set(); const out = [];
        for (const r of rates) {
            for (const t of Object.keys(rateFieldMap(r))) {
                if (t !== 'BAS' && !seen.has(t)) { seen.add(t); out.push(t); }
            }
        }
        return out;
    }, [rates]);

    return (
        <Box sx={{ padding: '24px', maxWidth: '1100px', margin: '0 auto' }}>
            <Typography variant='h4' sx={{ mb: 1 }}>LCL Tariff Tiers Import</Typography>
            <Typography variant='body1' sx={{ mb: 3, color: 'text.secondary' }}>
                Upload a FAM LCL tariff Excel together with its CargoFive charges JSON. The tool restores the full
                BAS CBM-tier ladder (CargoFive only keeps the first band), then lets you preview and add the rates
                to a workspace.
            </Typography>

            {error && <Alert severity='error' sx={{ mb: 3 }}>{error}</Alert>}

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box {...getRootProps()} sx={{
                        border: '2px dashed', borderColor: isDragActive ? 'primary.main' : 'grey.300', borderRadius: '8px',
                        p: '40px', textAlign: 'center', cursor: processing ? 'not-allowed' : 'pointer',
                        backgroundColor: isDragActive ? 'action.hover' : 'background.paper', opacity: processing ? 0.6 : 1,
                    }}>
                        <input {...getInputProps()} />
                        <FontAwesomeIcon icon={faCloudArrowUp} style={{ fontSize: 44, color: '#1976d2', marginBottom: 12 }} />
                        <Typography variant='body1'>Drag & drop the tariff <b>.xlsx</b> (and optionally the CargoFive <b>.json</b>) here, or click to select</Typography>
                        <Typography variant='caption' color='text.secondary'>Excel required. CargoFive JSON optional — attach it for the full surcharge set; without it you get BAS tiers + EFS only.</Typography>
                    </Box>
                    <Box sx={{ mt: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Chip color={excelFile ? 'success' : 'default'} variant={excelFile ? 'filled' : 'outlined'} label={excelFile ? `Excel: ${excelFile.name}` : 'Excel: none'} />
                        <Chip color={jsonFile ? 'success' : 'default'} variant={jsonFile ? 'filled' : 'outlined'} label={jsonFile ? `JSON: ${jsonFile.name}` : 'JSON: none (optional)'} />
                        {excelFile && !jsonFile && (
                            <>
                                <TextField size='small' label='Valid from' type='date' value={validFrom} onChange={e => setValidFrom(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
                                <TextField size='small' label='Valid until' type='date' value={validUntil} onChange={e => setValidUntil(e.target.value)} InputLabelProps={{ shrink: true }} sx={{ width: 160 }} />
                            </>
                        )}
                        <Box sx={{ flexGrow: 1 }} />
                        <Button variant='contained' disabled={!!processing || !excelFile} onClick={onConvert}>
                            {processing ? <><CircularProgress size={16} sx={{ mr: 1 }} />{processing}</> : 'Convert'}
                        </Button>
                    </Box>
                </CardContent>
            </Card>

            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth='xl' fullWidth>
                <DialogTitle>Converted rates preview</DialogTitle>
                <DialogContent dividers>
                    {stats && (
                        <Alert severity={(stats.mismatches?.length || stats.limited) ? 'warning' : 'success'} sx={{ mb: 2 }}>
                            <b>{stats.total}</b> rates &nbsp;|&nbsp; BAS tiered: <b>{stats.enriched}</b> &nbsp;|&nbsp; transit times filled: <b>{stats.ttFilled}</b>
                            &nbsp;|&nbsp; emails: <b>{stats.emailsFilled}</b>
                            {stats.limited && <div style={{ marginTop: 4 }}><b>Excel-only:</b> BAS + EFS for all routes{stats.surchargesApplied ? <>, plus prose surcharges parsed for <b>{stats.surchargesApplied}</b> origin(s)</> : null}. Attach the CargoFive JSON for the full surcharge set on every route.</div>}
                            {stats.unlabeledBlocks?.length > 0 && (
                                <div style={{ marginTop: 6, color: '#b26a00' }}>
                                    <b>⚠ {stats.unlabeledBlocks.length} unlabeled surcharge block(s)</b> — found surcharges with no origin header, so they were NOT applied. Add an "AGENT - ORIGIN" header above them in the Excel:
                                    <ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>
                                        {stats.unlabeledBlocks.map((b, i) => (
                                            <li key={i}>{b.sheet}, column {b.column}, rows {b.rows}: {b.titles.join(', ')}</li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {Object.keys(stats.unmatched || {}).length > 0 && <> &nbsp;|&nbsp; <span style={{ color: '#b26a00' }}>{stats.limited ? 'skipped — port not resolved by name (attach the JSON, or it needs a synonym)' : 'unmatched — BAS left single-band'}: {Object.keys(stats.unmatched).join(', ')}</span></>}
                            {stats.mismatches?.length > 0 && <> &nbsp;|&nbsp; <span style={{ color: '#b26a00' }}>band-1 mismatches: {stats.mismatches.length} (left unchanged)</span></>}
                            {stats.sheets && <div style={{ marginTop: 4, fontSize: 12 }}>Sheets: {stats.sheets.map(s => `${s.name} (${s.routes})`).join(', ')}</div>}
                        </Alert>
                    )}

                    <TableContainer component={Paper} variant='outlined' sx={{ maxHeight: 460, overflow: 'auto' }}>
                        <Table size='small' stickyHeader sx={{ '& td, & th': { whiteSpace: 'nowrap', px: 1 } }}>
                            <TableHead>
                                <TableRow>
                                    {['Origin', 'Dest', 'Supplier', 'TT', 'Valid from', 'Valid until'].map(h => <TableCell key={h}>{h}</TableCell>)}
                                    {['BAS', ...surchargeTitles].map(t => (
                                        <TableCell key={t} sx={{ verticalAlign: 'top', minWidth: t === 'BAS' ? 300 : 96 }}>
                                            <Box sx={{ fontWeight: 700, fontSize: 11 }}>{t}</Box>
                                            <Select variant='standard' value={columnMethod(t)} onChange={e => changeMethod(t, e.target.value)}
                                                sx={{ fontSize: 10, '& .MuiSelect-select': { py: 0 } }}>
                                                <MenuItem value='per-unit-type' sx={{ fontSize: 11 }}>per W/M</MenuItem>
                                                <MenuItem value='flat' sx={{ fontSize: 11 }}>per shipment</MenuItem>
                                            </Select>
                                        </TableCell>
                                    ))}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rates.slice(0, PREVIEW_LIMIT).map((r, i) => {
                                    const fm = rateFieldMap(r);
                                    const bas = fm.BAS || '';
                                    const tiered = bas.includes('?');
                                    return (
                                        <TableRow key={r.id || i} hover>
                                            <TableCell>{r.source?.id || ''}</TableCell>
                                            <TableCell>{r.destination?.id || ''}</TableCell>
                                            <TableCell>{r.supplier?.organization || ''}</TableCell>
                                            <TableCell>{r.offer?.transitTime ?? ''}</TableCell>
                                            <TableCell>{fmtDate(r.offer?.validFrom)}</TableCell>
                                            <TableCell>{fmtDate(r.offer?.validUntil)}</TableCell>
                                            <TableCell sx={{ p: 0.5 }} title={tiered ? 'per-unit tiers (W/M)' : 'not tiered'}>
                                                <TextField variant='standard' fullWidth value={bas}
                                                    onChange={e => editCharge(i, 'BAS', e.target.value)}
                                                    InputProps={{ disableUnderline: false, sx: { fontFamily: 'monospace', fontSize: 11, color: tiered ? 'success.main' : 'warning.main' } }}
                                                    sx={{ minWidth: 300 }} />
                                            </TableCell>
                                            {surchargeTitles.map(t => (
                                                <TableCell key={t} sx={{ p: 0.5 }}>
                                                    <TextField variant='standard' value={fm[t] || ''} placeholder='·'
                                                        onChange={e => editCharge(i, t, e.target.value)}
                                                        InputProps={{ disableUnderline: !fm[t], sx: { fontFamily: 'monospace', fontSize: 11 } }}
                                                        sx={{ minWidth: 90 }} />
                                                </TableCell>
                                            ))}
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                    <Typography variant='caption' color='text.secondary'>
                        {surchargeTitles.length} surcharge column(s){rates.length > PREVIEW_LIMIT ? ` — showing first ${PREVIEW_LIMIT} of ${rates.length}` : ''}.
                    </Typography>

                    {addResult && (
                        <Alert severity={addResult.error || addResult.failed ? 'warning' : 'success'} sx={{ mt: 2 }}>
                            {addResult.deleted ? <>Overrode <b>{addResult.deleted}</b> existing, </> : null}
                            added <b>{addResult.ok}</b> rates{addResult.org ? <> to <b>{addResult.org}</b></> : null}{addResult.failed ? `, ${addResult.failed} failed` : ''}{addResult.error ? ` — ${addResult.error}` : ''}.
                        </Alert>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
                    <FormControl size='small' sx={{ minWidth: 260 }} disabled={!orgs || !!adding}>
                        <InputLabel>Target workspace</InputLabel>
                        <Select label='Target workspace' value={orgId} onChange={e => setOrgId(e.target.value)}>
                            {(orgs || []).map(o => <MenuItem key={o.id} value={o.id}>{o.title || o.alias}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControlLabel
                        sx={{ ml: 1 }}
                        control={<Checkbox checked={overrideExisting} onChange={e => setOverrideExisting(e.target.checked)} />}
                        title='Delete existing rates with the same supplier, route and transshipment whose validity overlaps, before adding'
                        label={<Typography variant='caption'>Override existing (supplier · route · validity · transshipment)</Typography>}
                    />
                    <Box sx={{ flexGrow: 1 }} />
                    <Button onClick={() => setPreviewOpen(false)} color='inherit'>Close</Button>
                    <Button variant='contained' color='primary' disabled={!orgId || !!adding} onClick={onAdd}>
                        {adding ? <><CircularProgress size={16} sx={{ mr: 1 }} />{adding}</> : `${overrideExisting ? 'Override & add' : 'Add'} ${rates.length} rates`}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default LclTariffTiers;
